/* ============================================================
   ĒTHA — Dosha quiz landing

   The campaign version of the Dosha finder: the same four questions and the
   same scoring, in a page rather than a dialog. There is nothing to open and
   nothing to close. The quiz is on the screen when the page paints and the way
   out is the result's call to action.

   Flow: intro -> q1 -> q2 -> q3 -> q4 -> result. Screen zero is the intro,
   whose one button moves the quiz to question one; from there each answer
   advances by itself.

   Which campaign this is comes from the section's data-intent attribute
   ("sleep" or "focus"); it is pinned by the page template and rides along on
   every analytics payload so the two funnels stay apart.

   No dependencies. Nothing is loaded from anywhere.
   ============================================================ */

(function () {
  'use strict';

  var DOSHAS = ['vata', 'pitta', 'kapha'];
  var QUESTION_KEYS = ['energy', 'body', 'appetite', 'skin'];

  /* When two Doshas tie at 2–2 the steadier trait decides, in this order.
     A third and fourth entry can never be reached by four answers over three
     Doshas — the only possible tie is two-and-two — but they are the approved
     order and they cost nothing to keep. */
  var TIE_BREAK = ['skin', 'appetite', 'energy', 'body'];

  /* The four questions, in order. The intro is screen zero and deliberately
     not in this list: it carries no "N of 4" counter and no answer, so the
     only thing that indexes it is the flow (intro -> q1) in showStart below. */
  var SCREEN_ORDER = ['q1', 'q2', 'q3', 'q4'];

  /* ---------------------------------------------------------
     The scoring. Deliberately the only thing in this file that
     does arithmetic, so it can be read — and tested — on its own.
     Each answer is one point. The highest score is the result.
     Never a combined result, never a stronger claim than the copy makes.

     Byte-for-byte the popup's function, so the two agree on all 81
     combinations. Both are exposed (EthaDoshaFinder.score /
     EthaDoshaLanding.score) precisely so that can be checked.
     --------------------------------------------------------- */

  function scoreAnswers(answers) {
    var counts = { vata: 0, pitta: 0, kapha: 0 };
    var i;

    for (i = 0; i < QUESTION_KEYS.length; i++) {
      var chosen = answers[QUESTION_KEYS[i]];
      if (Object.prototype.hasOwnProperty.call(counts, chosen)) counts[chosen] += 1;
    }

    var highest = Math.max(counts.vata, counts.pitta, counts.kapha);
    var tied = DOSHAS.filter(function (dosha) { return counts[dosha] === highest; });

    if (tied.length === 1) {
      return { dosha: tied[0], counts: counts, tieBroken: false };
    }

    for (i = 0; i < TIE_BREAK.length; i++) {
      var answer = answers[TIE_BREAK[i]];
      if (tied.indexOf(answer) !== -1) {
        return { dosha: answer, counts: counts, tieBroken: true };
      }
    }

    return { dosha: tied[0], counts: counts, tieBroken: true };
  }

  /* ---------------------------------------------------------
     Analytics. Two destinations, neither of them a library and neither of them
     carrying anything personal — only the campaign, which screen, which answer,
     which Dosha.

     1. Shopify.analytics.publish — the store's actual analytics surface. This
        store has no GTM container and no gtag, so Customer Events is the only
        route to GA4. A custom pixel in Settings → Customer events subscribes to
        these names and forwards them. Shopify runs the pixel behind the shop's
        own consent state, which is why nothing here checks consent.
     2. a DOM event of the same name, for anything else on the page.

     The names are the popup's with `landing` in place of `finder`, so campaign
     traffic never lands in the popup's funnel.
     --------------------------------------------------------- */

  function track(name, detail) {
    var payload = detail || {};

    try {
      if (window.Shopify && window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch (error) {
      /* An analytics destination must never take the quiz down with it. */
    }

    try {
      document.dispatchEvent(new CustomEvent(name, { detail: payload }));
    } catch (error) {
      /* CustomEvent is present everywhere this ships; a failure here must
         never take the quiz down with it. */
    }
  }

  /* ---------------------------------------------------------
     One landing quiz
     --------------------------------------------------------- */

  function createLanding(root) {
    var screens = {};
    var current = null;
    var answers = {};
    var warmed = false;
    var advanceTimer = null;

    var intent = root.getAttribute('data-intent') || 'sleep';
    var live = root.querySelector('[data-dl-live]');
    var ctaTemplate = root.querySelector('[data-dl-cta-template]');
    var startTemplate = root.querySelector('[data-dl-start-template]');

    Array.prototype.forEach.call(root.querySelectorAll('[data-dl-screen]'), function (node) {
      screens[node.getAttribute('data-dl-screen')] = node;
    });

    /* -------- photographs --------
       The photograph markup comes from the shared `etha-dosha-finder-photo`
       snippet, so every image arrives lazy and carries data-df-photo.

       Question one is the first photograph a visitor sees, so it is switched
       to eager straight away, while the intro (which has no photograph of its
       own) is being read. The other six stay lazy until the first answer
       lands: a hidden eager image does load, which is what makes question two
       arrive before the visitor does, but starting all seven at once would only
       have them competing with question one. */

    function warmPhoto(screenName) {
      var screen = screens[screenName];
      if (!screen) return;
      Array.prototype.forEach.call(screen.querySelectorAll('img[data-df-photo]'), function (img) {
        img.loading = 'eager';
      });
    }

    function warmAllPhotos() {
      if (warmed) return;
      warmed = true;
      Object.keys(screens).forEach(warmPhoto);
    }

    /* -------- the two buttons --------
       The aura button's animation loop lives for as long as its element is in
       the document, so a button is put into the screen on show and taken out
       again when another screen arrives. That is what keeps one loop running,
       and only while it can be seen. The vendored component notices the new
       node by itself and starts it.

       Two slots, one rule: the intro's [data-dl-start-slot] gets the <button>
       that moves the quiz on, a result's [data-dl-cta-slot] gets the <a> that
       leaves it. The result's href is rendered by the section — the page
       template pins the campaign, so Liquid already knows both facet values
       and writes the finished filter URL onto each result's slot. */

    function clearButtons() {
      Array.prototype.forEach.call(root.querySelectorAll('[data-dl-cta-slot], [data-dl-start-slot]'), function (slot) {
        while (slot.firstChild) slot.removeChild(slot.firstChild);
      });
    }

    function placeButton(screenName) {
      clearButtons();

      var screen = screens[screenName];
      if (!screen) return;

      var startSlot = screen.querySelector('[data-dl-start-slot]');
      if (startSlot && startTemplate) {
        startSlot.appendChild(startTemplate.content.firstElementChild.cloneNode(true));
      }

      if (!ctaTemplate) return;
      var slot = screen.querySelector('[data-dl-cta-slot]');
      if (!slot) return;

      var button = ctaTemplate.content.firstElementChild.cloneNode(true);
      button.setAttribute('href', slot.getAttribute('data-dl-cta-href'));
      button.setAttribute('data-dl-cta', slot.getAttribute('data-dl-cta-dosha'));
      slot.appendChild(button);
    }

    /* -------- screens -------- */

    function announce(name) {
      if (!live) return;
      var index = SCREEN_ORDER.indexOf(name);
      if (name === 'intro') {
        /* Screen zero is the one on screen at load. Nothing has changed yet,
           so there is nothing for a live region to interrupt anybody with. */
        live.textContent = '';
      } else if (index !== -1) {
        live.textContent = 'Question ' + (index + 1) + ' of 4.';
      } else {
        live.textContent = 'Your result.';
      }
    }

    function showScreen(name) {
      if (!screens[name]) return;

      /* The heading of an arriving screen takes focus so a screen reader reads
         it out — but not on the first one. Nobody asked for this page yet at
         that point, and moving focus into a section on load is how you lose a
         visitor's place. */
      var isFirst = current === null;

      Object.keys(screens).forEach(function (key) {
        screens[key].hidden = key !== name;
      });
      current = name;

      clearChosen();
      placeButton(name);

      var body = screens[name].querySelector('.etha-dl__body');
      if (body) body.scrollTop = 0;

      if (!isFirst) {
        var focusTarget = screens[name].querySelector('[data-dl-focus]');
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      }

      announce(name);
    }

    function clearChosen() {
      Array.prototype.forEach.call(root.querySelectorAll('[data-dl-chosen]'), function (button) {
        button.removeAttribute('data-dl-chosen');
      });
    }

    function prefersReducedMotion() {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }

    /* -------- answering -------- */

    function answer(key, dosha, button) {
      if (advanceTimer !== null) return;   /* a second tap during the pause */

      answers[key] = dosha;
      if (button) button.setAttribute('data-dl-chosen', '');
      warmAllPhotos();

      var index = QUESTION_KEYS.indexOf(key);
      track('etha_dosha_landing_answer', {
        intent: intent,
        question: index + 1,
        question_key: key,
        answer: dosha
      });

      var go = function () {
        advanceTimer = null;
        if (index < SCREEN_ORDER.length - 1) showScreen(SCREEN_ORDER[index + 1]);
        else finish();
      };

      /* long enough to see the pin fill, short enough to still feel like the
         answer moved you on by itself */
      if (prefersReducedMotion()) go();
      else advanceTimer = window.setTimeout(go, 240);
    }

    function finish() {
      var result = scoreAnswers(answers);
      showScreen('result-' + result.dosha);
      track('etha_dosha_landing_result', {
        intent: intent,
        dosha: result.dosha,
        score_vata: result.counts.vata,
        score_pitta: result.counts.pitta,
        score_kapha: result.counts.kapha,
        tie_broken: result.tieBroken
      });
    }

    /* -------- events inside the quiz -------- */

    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      /* Screen zero's only control. No event of its own — the campaign's
         event names are fixed, and the view is already tracked on load. */
      var start = target.closest('[data-dl-start]');
      if (start && root.contains(start)) {
        showScreen('q1');
        return;
      }

      var answerButton = target.closest('[data-dl-answer]');
      if (answerButton && root.contains(answerButton)) {
        answer(answerButton.getAttribute('data-dl-key'), answerButton.getAttribute('data-dl-answer'), answerButton);
        return;
      }

      /* The one way out. Tracked before the navigation, never instead of it. */
      var cta = target.closest('[data-dl-cta]');
      if (cta) {
        track('etha_dosha_landing_cta_click', {
          intent: intent,
          dosha: cta.getAttribute('data-dl-cta')
        });
      }
    });

    /* The intro carries no photograph, so question one's is warmed while the
       intro is being read — it arrives before the visitor does, and the ad
       landing's first paint costs no image weight at all. */
    warmPhoto('q1');
    showScreen('intro');
    track('etha_dosha_landing_view', { intent: intent });

    return { root: root };
  }

  /* ---------------------------------------------------------
     Finding the quiz. The controller is cached on the element, so a
     theme-editor section reload gets a fresh one rather than a second set of
     listeners on the same markup.
     --------------------------------------------------------- */

  function init() {
    var root = document.querySelector('[data-etha-dosha-landing]');
    if (!root || root.ethaDoshaLanding) return;
    root.ethaDoshaLanding = createLanding(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  /* Scored from anywhere — QA compares this against EthaDoshaFinder.score. */
  window.EthaDoshaLanding = {
    score: scoreAnswers
  };
}());
